import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createVNode as _createVNode, normalizeClass as _normalizeClass, createBlock as _createBlock, Fragment as _Fragment } from "vue";
const _hoisted_1 = {
  class: "flex flex-col items-center"
};
const _hoisted_2 = {
  class: "text-sm text-gray-800 font-medium mb-4"
};
const _hoisted_3 = {
  key: 0,
  class: "text-2xl font-bold text-black mb-1"
};
const _hoisted_4 = {
  class: "text-sm text-gray-500 mb-4"
};
const _hoisted_5 = {
  key: 1,
  class: "text-xs text-gray-400 mb-6"
};
const _hoisted_6 = {
  key: 2,
  class: "w-full"
};
const _hoisted_7 = {
  key: 3,
  class: "w-full"
};
import { reactive, computed, toRef, ref } from 'vue';
import { useMaxioSubscriptionStore } from './useMaxioSubscriptionStore.js';
import StartSubscriptionModel from './StartSubscriptionModel.vue';
import PlanSelection from '@/modules/onboarding/components/PlanSelection.vue';
import LlConfirmationDialog from '@/components/LlConfirmationDialog.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import { usePricingCalculations } from './usePricingCalculations.js';
import { storeToRefs } from 'pinia';
import { useMeStore } from '@/services/UserService/useMeStore';
export default {
  __name: 'MaxioProductDeal',
  props: {
    alerts: {
      type: Object,
      required: true
    },
    product: {
      type: Object,
      required: true
    },
    plan: {
      type: Object,
      required: true
    },
    actionLabel: {
      type: String,
      required: false
    },
    actionDisabled: {
      type: Boolean,
      required: false
    },
    selectedProduct: {
      type: Object,
      required: false
    },
    interval: {
      type: String,
      required: false
    },
    seats: {
      type: [Number, String],
      required: false
    },
    isSmallScreen: {
      type: Boolean,
      required: false
    }
  },
  emits: ['subscription-updated'],
  setup(__props, {
    emit: __emit
  }) {
    const props = __props;
    const productDealState = reactive({
      loading: false,
      selecting: false,
      subscription: {},
      confirmSubscriptionModal: false,
      showPlanSelection: false
    });
    const confirmationDialog = ref(null);
    const confirmationState = reactive({
      title: '',
      message: '',
      pendingAction: null
    });
    const emit = __emit;

    // Access feature flags
    const meStore = useMeStore();
    const {
      features
    } = storeToRefs(meStore);
    const maxioCompanySubscriptionStore = useMaxioSubscriptionStore();
    maxioCompanySubscriptionStore.alerts = props.alerts;

    // Use the shared pricing calculations
    const {
      priceBreakdown,
      totalAmount,
      formattedDisplayAmount,
      billingPeriodText
    } = usePricingCalculations({
      plan: toRef(props, 'plan'),
      interval: toRef(props, 'interval'),
      seats: toRef(props, 'seats'),
      subscription: computed(() => maxioCompanySubscriptionStore.subscription)
    });

    // Helper function to show confirmation dialog for plan updates
    const showPlanChangeConfirmation = async newPlan => {
      const currentSubscription = maxioCompanySubscriptionStore.subscription;
      const currentPlanName = currentSubscription?.product?.label || 'Current Plan';
      const newPlanName = newPlan?.label || 'Selected Plan';

      // Set confirmation dialog content
      confirmationState.title = 'Confirm Plan Change';
      confirmationState.message = `Are you sure you want to change from "${currentPlanName}" to "${newPlanName}"? This will update your subscription and may affect your billing.`;

      // Store the pending plan for the update
      confirmationState.pendingAction = {
        plan: newPlan
      };

      // Show the confirmation dialog
      const confirmed = await confirmationDialog.value?.open();
      return confirmed;
    };

    // Confirmation dialog handlers
    const onConfirmPlanChange = async () => {
      if (!confirmationState.pendingAction) return;
      const {
        plan
      } = confirmationState.pendingAction;
      try {
        productDealState.selecting = true;
        await maxioCompanySubscriptionStore.changePlan(plan?.id);
        emit('subscription-updated');
      } catch (error) {
        props.alerts?.error('Failed to update subscription');
      } finally {
        productDealState.selecting = false;
        confirmationState.pendingAction = null;
      }
    };
    const onCancelPlanChange = () => {
      confirmationState.pendingAction = null;
      // No action needed - subscription remains unchanged
    };
    const selectSubscriptionPlan = async () => {
      if (!maxioCompanySubscriptionStore?.subscription?.product) {
        // No existing subscription - handle new subscription creation (no confirmation needed)
        if (!props.isSmallScreen) {
          productDealState.showPlanSelection = true;
        } else {
          productDealState.selecting = true;
          await maxioCompanySubscriptionStore.createSubscriptionComponent({
            productId: props?.plan?.id,
            seats: maxioCompanySubscriptionStore?.subscription?.seatsRequired || maxioCompanySubscriptionStore?.subscription?.plan?.currentPaidSeats || maxioCompanySubscriptionStore?.subscription?.plan?.seats || 1,
            coupon: maxioCompanySubscriptionStore?.subscription?.couponCode || '',
            email: maxioCompanySubscriptionStore?.subscription?.billingEmail || ''
          });
          productDealState.selecting = false;
          emit('subscription-created');
        }
      } else if (maxioCompanySubscriptionStore.isAwaitingSubscription || maxioCompanySubscriptionStore.isCanceledSubscription) {
        // Show confirmation for updating awaiting/canceled subscription
        await showPlanChangeConfirmation(props.plan);
      } else {
        productDealState.confirmSubscriptionModal = true;
      }
    };
    const cancel = () => {
      productDealState.confirmSubscriptionModal = false;
    };
    return (_ctx, _cache) => {
      return _openBlock(), _createElementBlock(_Fragment, null, [_createElementVNode("div", _hoisted_1, [_createElementVNode("div", {
        class: _normalizeClass(["pricing-card bg-white rounded-lg shadow-sm border border-gray-100 p-6 text-center", {
          'w-60': __props.isSmallScreen,
          'w-72': !__props.isSmallScreen
        }])
      }, [_createElementVNode("div", _hoisted_2, _toDisplayString(__props.plan?.label), 1), !_unref(features)?.customPricing ? (_openBlock(), _createElementBlock("div", _hoisted_3, [_createTextVNode(" $ " + _toDisplayString(_unref(formattedDisplayAmount)) + " ", 1), _createElementVNode("span", _hoisted_4, _toDisplayString(_unref(billingPeriodText)), 1)])) : _createCommentVNode("", true), !_unref(features)?.customPricing ? (_openBlock(), _createElementBlock("div", _hoisted_5, _toDisplayString(_unref(priceBreakdown)), 1)) : _createCommentVNode("", true), __props.actionLabel ? (_openBlock(), _createElementBlock("div", _hoisted_6, [_createVNode(LlSecondaryButton, {
        color: "primary",
        size: "large",
        block: "",
        disabled: __props.actionDisabled || productDealState.loading || productDealState.selecting,
        onClick: _cache[0] || (_cache[0] = $event => selectSubscriptionPlan(__props.plan))
      }, {
        default: _withCtx(() => [_createTextVNode(_toDisplayString(productDealState.selecting ? 'Selecting...' : __props.actionLabel), 1)]),
        _: 1
      }, 8, ["disabled"])])) : (_openBlock(), _createElementBlock("div", _hoisted_7, [...(_cache[3] || (_cache[3] = [_createElementVNode("div", {
        class: "text-sm text-gray-600 font-medium py-2"
      }, "Selected", -1)]))]))], 2)]), productDealState.confirmSubscriptionModal ? (_openBlock(), _createBlock(StartSubscriptionModel, {
        key: 0,
        alerts: props.alerts,
        plan: props.plan,
        onCancel: cancel,
        onSubscriptionUpdated: _cache[1] || (_cache[1] = $event => emit('subscription-updated'))
      }, null, 8, ["alerts", "plan"])) : _createCommentVNode("", true), productDealState.showPlanSelection ? (_openBlock(), _createBlock(PlanSelection, {
        key: 1,
        onCancel: _cache[2] || (_cache[2] = $event => productDealState.showPlanSelection = false)
      })) : _createCommentVNode("", true), _createVNode(LlConfirmationDialog, {
        ref_key: "confirmationDialog",
        ref: confirmationDialog,
        title: confirmationState.title,
        message: confirmationState.message,
        "confirm-text": "Confirm Change",
        "toggle-text": "Cancel",
        "show-warning-icon": true,
        onConfirm: onConfirmPlanChange,
        onCancel: onCancelPlanChange,
        "data-cy": "plan-change-confirmation"
      }, null, 8, ["title", "message"])], 64);
    };
  }
};